import React, { useState } from 'react';
import { Modal, Form, Input, Typography, Alert, Space } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;

interface PasswordChangeModalProps {
  visible: boolean;
  username: string;
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({ visible, username }) => {
  const [form] = Form.useForm();
  const { confirmPasswordChange, clearPasswordResetState, error, isLoading } = useAuthStore();
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);

  const handleSubmit = async (values: { newPassword: string; confirmPassword: string }) => {
    try {
      await confirmPasswordChange(values.newPassword);
      // Modal will close automatically after successful password change
    } catch (err) {
      console.error('Password change error:', err);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    clearPasswordResetState();
  };

  const validatePassword = (_: unknown, value: string) => {
    if (!value) {
      return Promise.reject(new Error('Please enter a password'));
    }
    if (value.length < 8) {
      return Promise.reject(new Error('Password must be at least 8 characters'));
    }
    
    // Check password strength
    let strength: 'weak' | 'medium' | 'strong' | null = null;
    if (value.length >= 12 && /[A-Z]/.test(value) && /[a-z]/.test(value) && /[0-9]/.test(value) && /[^A-Za-z0-9]/.test(value)) {
      strength = 'strong';
    } else if (value.length >= 8 && /[A-Za-z]/.test(value) && /[0-9]/.test(value)) {
      strength = 'medium';
    } else {
      strength = 'weak';
    }
    setPasswordStrength(strength);
    
    return Promise.resolve();
  };

  const validateConfirmPassword = ({ getFieldValue }: any) => ({
    validator(_: unknown, value: string) {
      if (!value || getFieldValue('newPassword') === value) {
        return Promise.resolve();
      }
      return Promise.reject(new Error('Passwords do not match'));
    },
  });

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'strong': return '#52c41a';
      default: return 'transparent';
    }
  };

  return (
    <Modal
      title={
        <Space>
          <LockOutlined style={{ color: '#1890ff' }} />
          <Title level={4} style={{ margin: 0 }}>Set New Password</Title>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      closable={false}
      maskClosable={false}
      width={480}
      centered
    >
      <div style={{ marginBottom: '16px' }}>
        <Alert
          message="Password Change Required"
          description={`Welcome, ${username}! Your account requires a new password. Please set a secure password to continue.`}
          type="info"
          showIcon
        />
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
      >
        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[{ validator: validatePassword }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter your new password"
            autoComplete="new-password"
          />
        </Form.Item>

        {passwordStrength && (
          <div style={{ marginBottom: '16px' }}>
            <Space>
              <Text type="secondary" style={{ fontSize: '12px' }}>Password strength:</Text>
              <Text style={{ color: getPasswordStrengthColor(), fontWeight: 'bold', fontSize: '12px' }}>
                {passwordStrength.toUpperCase()}
              </Text>
            </Space>
          </div>
        )}

        <div style={{ marginBottom: '16px', padding: '12px', background: '#f0f8ff', borderRadius: '6px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Password requirements:
          </Text>
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
            <li>Minimum 8 characters</li>
            <li>Strong password recommended (12+ characters with uppercase, lowercase, numbers, and symbols)</li>
          </ul>
        </div>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          rules={[validateConfirmPassword]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm your new password"
            autoComplete="new-password"
          />
        </Form.Item>

        {error && (
          <Alert
            message="Password Change Failed"
            description={error.message}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: '4px 15px',
                height: '32px',
                fontSize: '14px',
                borderRadius: '6px',
                border: '1px solid #d9d9d9',
                background: '#fff',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '4px 15px',
                height: '32px',
                fontSize: '14px',
                borderRadius: '6px',
                border: 'none',
                background: '#1890ff',
                color: '#fff',
                cursor: isLoading ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoading ? 'Changing Password...' : 'Set New Password'}
            </button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PasswordChangeModal;

